import React, { useEffect, useState } from "react";
import styled from "styled-components";
import ResumeLogo from "../../assets/svg/Services/ResumeIcon";
import InterviewLogo from "../../assets/svg/Services/InterviewIcon";
import MatchingLogo from "../../assets/svg/Services/CareerIcon";
import CounselingLogo from "../../assets/svg/Services/MentorshipIcon";
import BrandingLogo from "../../assets/svg/Services/RollerIcon";
import WorkshopsLogo from "../../assets/svg/Services/SkillIcon";
import { useNavigate } from "react-router-dom";
import { isUserLoggedIn } from "../../utils/AuthHandler";

export default function Services() {
  const [services, setServices] = useState([]);
  const [activeService, setActiveService] = useState(null);
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(null);

  useEffect(() => {
    const dummyServices = [
      {
        id: 1,
        title: "Internship Placement",
        description:
          "We make finding internships easy, fast, and reliable by connecting students with top companies. Gain hands-on experience, real projects, and industry exposure without the hassle.",
        logo: ResumeLogo,
        color: "#008394",
      },
      {
        id: 2,
        title: "Talent Sourcing & Recruitment",
        description:
          "We help businesses, organizations, and consultants find top talent, from skilled interns to entry-level and senior hires. Our data-driven approach ensures the right match for your hiring needs, saving time and improving retention.",
        logo: InterviewLogo,
        color: "#EEA746",
      },
      {
        id: 3,
        title: "Strategy & Solutions Challenge",
        description:
          "We turn real business challenges into case studies for students to solve, providing companies with innovative solutions while giving students hands-on experience and job opportunities.",
        logo: MatchingLogo,
        color: "#4A4A4A",
      },
      {
        id: 4,
        title: "Career Coaching & Job Readiness",
        description:
          "Our expert-led coaching helps students and graduates refine their resumes, prepare for interviews, and develop networking strategies. We also partner with universities to run career development workshops.",
        logo: CounselingLogo,
        color: "#8A2BE2",
      },
      {
        id: 5,
        title: "Employer Branding",
        description:
          "We help companies attract top talent through targeted digital marketing, on-campus activations, and networking events. Universities can partner with us to connect their students with top employers.",
        logo: BrandingLogo,
        color: "#008394",
      },
      {
        id: 6,
        title: "Consulting & Custom Workforce Solutions",
        description:
          "We provide businesses with tailored hiring and workforce solutions, from recruitment consulting to leadership development.",
        logo: WorkshopsLogo,
        color: "#EEA746",
      },
    ];
    setIsLoggedIn(isUserLoggedIn());
    setServices(dummyServices);
  }, []);

  const handleMouseEnter = (id) => {
    setActiveService(id);
  };

  const handleMouseLeave = () => {
    setActiveService(null);
  };

  const handleGetStarted = () => {
    navigate("/register");
  };

  return (
    <Wrapper id="services">
      <div className="whiteBg" style={{ padding: "80px 0" }}>
        <div className="container">
          <HeaderInfo>
            <h1 className="font40 darkGreenColor extraBold">Our Services</h1>
            <HeadingUnderline />
            <p className="font18">
              We provide a variety of services to support talents and employers
              in achieving their goals.
              <br />
              Browse through our offerings to discover how we can help you
              succeed.
            </p>
          </HeaderInfo>
          <ServicesGrid>
            {services.map((service) => (
              <ServiceCard
                key={service.id}
                onMouseEnter={() => handleMouseEnter(service.id)}
                onMouseLeave={handleMouseLeave}
                active={activeService === service.id}
                accentColor={service.color}
              >
                <LogoWrapper
                  accentColor={service.color}
                  active={activeService === service.id}
                >
                  <service.logo />
                </LogoWrapper>
                <ServiceTitle accentColor={service.color}>
                  {service.title}
                </ServiceTitle>
                <ResponsiveDescription>
                  <p>{service.description}</p>
                  {!isLoggedIn && (
                    <LearnMoreButton
                      onClick={handleGetStarted}
                      accentColor={service.color}
                      active={activeService === service.id}
                    >
                      Get Started
                    </LearnMoreButton>
                  )}
                </ResponsiveDescription>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </div>
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.section`
  width: 100%;
  background-color: #f9f9f9;
`;

const HeaderInfo = styled.div`
  margin-bottom: 50px;
  text-align: center;
  position: relative;

  h1 {
    margin-bottom: 20px;
    font-size: 2.5rem;
  }

  p {
    max-width: 700px;
    margin: 0 auto;
    line-height: 1.6;
  }

  @media (max-width: 860px) {
    text-align: center;
  }
`;

const HeadingUnderline = styled.div`
  width: 80px;
  height: 4px;
  background: linear-gradient(to right, #2ecc71, #1abc9c);
  margin: 0 auto 25px;
  border-radius: 2px;
`;

const ServicesGrid = styled.div`
  display: grid;
  gap: 12px;
  max-width: 1200px;
  margin: 0 auto;

  /* Force 2 columns on all mobile phones regardless of width */
  grid-template-columns: repeat(2, 1fr);

  /* Adjust card spacing for mid-sized mobile devices */
  @media (min-width: 480px) {
    gap: 16px;
  }

  /* Slightly larger gap for tablets */
  @media (min-width: 768px) {
    gap: 20px;
  }

  /* For desktop, switch to auto-fill with wider minimum card width */
  @media (min-width: 1024px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 30px;
  }
`;

const ServiceCard = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: ${(props) =>
    props.active
      ? `0 15px 30px rgba(0, 0, 0, 0.15), 0 0 0 2px ${props.accentColor}`
      : "0 8px 20px rgba(0, 0, 0, 0.08)"};
  padding: 15px 10px;
  text-align: center;
  transition: all 0.4s ease;
  display: flex;
  flex-direction: column;
  height: 100%;

  h3 {
    font-size: 16px;
    margin: 12px 0 8px;
    color: #333;
    font-weight: 600;
    transition: color 0.3s ease;
    color: ${(props) => (props.active ? props.accentColor : "#333")};
  }

  p {
    font-size: 14px;
    color: #666;
    line-height: 1.4;
    flex-grow: 1;
    margin-bottom: 12px;
  }

  /* Tablet adjustments */
  @media (min-width: 480px) {
    padding: 20px 15px;

    h3 {
      font-size: 18px;
      margin: 15px 0 10px;
    }
  }

  /* Desktop adjustments */
  @media (min-width: 1024px) {
    padding: 30px 25px;

    h3 {
      font-size: 20px;
      margin: 20px 0 15px;
    }

    p {
      font-size: 16px;
      line-height: 1.6;
      margin-bottom: 20px;
    }
  }

  &:hover {
    transform: translateY(-8px);
  }
`;

const ServiceTitle = styled.h3`
  font-size: 15px;
  margin: 10px 0 6px;
  padding: 0 5px;
  font-weight: 600;
  line-height: 1.3;
  color: ${(props) => (props.active ? props.accentColor : "#333")};
  transition: color 0.3s ease;
  /* Handle long titles better on small screens */
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;

  @media (min-width: 480px) {
    font-size: 17px;
    margin: 12px 0 8px;
    line-height: 1.4;
  }

  @media (min-width: 1024px) {
    font-size: 20px;
    margin: 20px 0 15px;
    padding: 0;
    line-height: 1.5;
  }
`;

const LogoWrapper = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.accentColor};
  border-radius: 50%;
  transition: all 0.3s ease;

  svg {
    width: 30px;
    height: 30px;
    color: ${(props) => (props.active ? "#fff" : "#555")};
    transition: all 0.3s ease;
  }

  @media (min-width: 480px) {
    width: 70px;
    height: 70px;

    svg {
      width: 35px;
      height: 35px;
    }
  }

  @media (min-width: 1024px) {
    width: 90px;
    height: 90px;

    svg {
      width: 45px;
      height: 45px;
    }
  }
`;

const LearnMoreButton = styled.button`
  background: ${(props) => (props.active ? props.accentColor : "transparent")};
  color: ${(props) => (props.active ? "#fff" : props.accentColor)};
  border: 2px solid ${(props) => props.accentColor};
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: auto;

  /* Smaller button for mobile */
  padding: 8px 14px;
  font-size: 12px;

  /* Medium size for tablets */
  @media (min-width: 480px) {
    padding: 9px 16px;
    font-size: 13px;
  }

  /* Original size for desktop */
  @media (min-width: 1024px) {
    padding: 10px 20px;
    font-size: 14px;
  }

  &:hover {
    background: ${(props) => props.accentColor};
    color: white;
  }
`;

const ResponsiveDescription = styled.div`
  display: block;

  @media (max-width: 768px) {
    display: ${(props) => (props.isExpanded ? "block" : "none")};
  }

  p {
    font-size: 16px;
    color: #666;
    line-height: 1.6;
    margin-bottom: 20px;
  }
`;
